import { useState } from 'react';
import { Link } from 'wouter';
import { AlertTriangle, ArrowLeft, Play, UserX } from 'lucide-react';
import { AdminAPI } from '@shared/types/api';
import { useToast } from '@/components/ux/Toast';
import { useConfirm } from '@/components/ConfirmModal';
import { useNoShowBatchAction, useNoShows, useRunNoShowReconciler } from '../api/useAdminNoShowFees';
import { RefreshButton, StatusChip, eventLabel, timeAgo, timeUntil } from '../components/ops/opsUi';
import { formatPrice } from '@/utils/priceUtils';

// No-show fees: the server-wide config and the cases it has produced. Modes
// are layered, each doing everything the one before it does:
// off → track → warn → charge. Change the config in
// server/src/services/noShow/config.ts; the mode alone can be flipped with
// NO_SHOW_FEE_MODE in the server environment.

const MODE_HELP: Record<AdminAPI.NoShowFeeMode, string> = {
  off: 'Nothing happens.',
  track: 'Open cases from tablemate reports. Staff only, guests are never contacted.',
  warn: 'Track, and email the guest that they were reported. No fee.',
  charge:
    'Warn, and the email announces the fee and its date. Charged after the grace period unless excused. Only bookings made with the disclaimer.',
};

const CASE_STATUS_STYLES: Record<string, string> = {
  detected: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
  notified: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  excused: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  charged: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  charge_failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

const STATUS_FILTERS: { key: AdminAPI.NoShowCaseStatus | undefined; label: string }[] = [
  { key: undefined, label: 'All' },
  { key: 'detected', label: 'Detected' },
  { key: 'notified', label: 'Notified' },
  { key: 'excused', label: 'Excused' },
  { key: 'charged', label: 'Charged' },
  { key: 'charge_failed', label: 'Charge failed' },
];

const INPUT =
  'px-2 py-1 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-sm';
const BTN =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50';

const errorMessage = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback);

const feeOf = (c: AdminAPI.NoShowCaseRow) => formatPrice(c.feeAmount, c.currency);

// One label for a set of cases: the amount when they all share a currency,
// otherwise just "the fee".
function feeLabel(cases: AdminAPI.NoShowCaseRow[]): string {
  const labels = new Set(cases.map(feeOf));
  return labels.size === 1 ? [...labels][0] : 'the fee';
}

function describeNext(c: AdminAPI.NoShowCaseRow, mode: AdminAPI.NoShowFeeMode): string {
  const fee = feeOf(c);
  switch (c.status) {
    case 'detected':
      return mode === 'warn' || mode === 'charge' ? 'Notice goes out on the next run' : 'Tracking only';
    case 'notified':
      if (!c.chargeAfter) return 'Warned only, will not be charged';
      if (mode !== 'charge') return `Charging paused (mode is ${mode})`;
      return `Charges ${fee} ${new Date(c.chargeAfter) > new Date() ? `in ${timeUntil(c.chargeAfter)}` : 'on the next run'}`;
    case 'charged':
      return `Charged ${fee} ${timeAgo(c.chargedAt)}`;
    case 'charge_failed':
      return `Charge failed: ${c.chargeError ?? 'unknown error'}`;
    case 'excused':
      return `Excused by ${c.excusedByName ?? 'staff'}${c.excuseReason ? `: ${c.excuseReason}` : ''}`;
  }
}

const ACTION_BTN =
  'px-2.5 py-1 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed';

const ACTION_LABELS: Record<AdminAPI.NoShowBatchAction, string> = {
  warn: 'Send warning',
  fee_notice: 'Send fee notice',
  charge: 'Charge now',
  excuse: 'Excuse',
  reopen: 'Reopen',
};

// Which actions make sense for a case in this state. Everything but `charged`
// can be warned, fee-noticed or charged; excuse/reopen depend on the state.
function actionsFor(c: AdminAPI.NoShowCaseRow): AdminAPI.NoShowBatchAction[] {
  if (c.status === 'charged') return [];
  const list: AdminAPI.NoShowBatchAction[] = ['warn', 'fee_notice', 'charge'];
  if (c.status !== 'excused') list.push('excuse');
  if (c.status === 'excused' || c.status === 'charge_failed') list.push('reopen');
  return list;
}

/**
 * One confirm + one batch call for any action on any number of cases. Used by
 * the per-row buttons (one id) and the multi-select bar (many).
 */
function useCaseActions() {
  const toast = useToast();
  const { confirm } = useConfirm();
  const batch = useNoShowBatchAction();

  const run = async (
    action: AdminAPI.NoShowBatchAction,
    cases: AdminAPI.NoShowCaseRow[],
    reason?: string
  ): Promise<boolean> => {
    if (cases.length === 0) return false;
    const n = cases.length;
    const who = n === 1 ? cases[0].userName : `${n} guests`;
    const fee = feeLabel(cases);
    const withoutDisclaimer = cases.filter((c) => !c.disclaimerAccepted).length;
    const disclaimerWarning =
      withoutDisclaimer > 0 && (action === 'fee_notice' || action === 'charge')
        ? ` ${n === 1 ? 'This booking was' : `${withoutDisclaimer} of these bookings were`} made WITHOUT the no-show disclaimer, so the guest never agreed to a fee.`
        : '';

    const copy: Record<AdminAPI.NoShowBatchAction, { title: string; message: string; danger: boolean }> = {
      warn: {
        title: `Send ${who} a warning?`,
        message: 'Emails them now that their table reported they did not turn up. No fee is mentioned, and any pending fee is withdrawn.',
        danger: false,
      },
      fee_notice: {
        title: `Send ${who} the ${fee} fee notice?`,
        message: `Emails them now that the ${fee} fee will be charged after the grace period unless they get in touch. Resets any grace period already running.${disclaimerWarning}`,
        danger: true,
      },
      charge: {
        title: `Charge ${who} ${fee} now?`,
        message: `Charges each saved payment method immediately, without waiting for the grace period. Cannot be undone here; refunds are done in Stripe.${disclaimerWarning}`,
        danger: true,
      },
      excuse: { title: `Excuse ${who}?`, message: 'Blocks any charge on these cases.', danger: false },
      reopen: { title: `Reopen ${who}?`, message: 'Puts the cases back in the queue.', danger: false },
    };
    const c = copy[action];
    if (action !== 'excuse') {
      const ok = await confirm({ title: c.title, message: c.message, confirmText: ACTION_LABELS[action], type: c.danger ? 'danger' : 'default' });
      if (!ok) return false;
    }

    try {
      const res = await batch.mutateAsync({ action, caseIds: cases.map((x) => x.id), reason });
      const { ok, failed } = res.data ?? { ok: [], failed: [] };
      const nameOf = (id: string) => cases.find((x) => x.id === id)?.userName ?? id.slice(0, 8);
      if (failed.length === 0) toast.success(`${ACTION_LABELS[action]}: ${ok.length} done`);
      else if (ok.length === 0) toast.error(`${ACTION_LABELS[action]} failed: ${failed.map((f) => `${nameOf(f.caseId)} (${f.error})`).join('; ')}`);
      else toast.error(`${ACTION_LABELS[action]}: ${ok.length} done, ${failed.length} failed: ${failed.map((f) => `${nameOf(f.caseId)} (${f.error})`).join('; ')}`);
      return ok.length > 0;
    } catch (err) {
      toast.error(errorMessage(err, `${ACTION_LABELS[action]} failed`));
      return false;
    }
  };

  return { run, isPending: batch.isPending };
}

function ExcuseForm({ onConfirm, onCancel, pending }: { onConfirm: (reason?: string) => void; onCancel: () => void; pending: boolean }) {
  const [reason, setReason] = useState('');
  return (
    <div className="mt-2 flex gap-2">
      <input
        className={`${INPUT} flex-1`}
        placeholder="Reason (optional), e.g. family emergency, told us in advance"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <button className={BTN} disabled={pending} onClick={() => onConfirm(reason.trim() || undefined)}>
        Confirm
      </button>
      <button className={BTN} onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

function CaseRow({
  c,
  mode,
  selected,
  onToggle,
  actions,
}: {
  c: AdminAPI.NoShowCaseRow;
  mode: AdminAPI.NoShowFeeMode;
  selected: boolean;
  onToggle: () => void;
  actions: ReturnType<typeof useCaseActions>;
}) {
  const [excusing, setExcusing] = useState(false);
  const available = actionsFor(c);

  return (
    <div className={`rounded-xl border p-3 ${selected ? 'border-primary-500 bg-primary-50/40 dark:bg-primary-900/10' : 'border-neutral-200 dark:border-neutral-700'}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {available.length > 0 && (
          <input type="checkbox" className="checkbox checkbox-sm checkbox-primary" checked={selected} onChange={onToggle} aria-label={`Select ${c.userName}`} />
        )}
        <Link
          href={`/users?search=${encodeURIComponent(c.userEmail ?? c.userName)}`}
          className="text-sm font-semibold hover:text-primary-500"
        >
          {c.userName}
        </Link>
        <StatusChip status={c.status} styles={CASE_STATUS_STYLES} />
        <span className="text-[11px] text-dark-muted">
          {eventLabel(c.eventTimeId)}
          {c.restaurantName ? ` · ${c.restaurantName}` : ''}
        </span>
        <span className="text-[11px] text-dark-muted ml-auto">
          {c.reportCount > 0 ? `${c.reportCount} report(s)` : 'flagged by staff'}
        </span>
      </div>
      <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-dark-muted">
        <span>{describeNext(c, mode)}</span>
        {!c.disclaimerAccepted && (
          <span className="inline-flex items-center gap-1 text-amber-600">
            <AlertTriangle className="w-3 h-3" />
            booked without the disclaimer
          </span>
        )}
        {c.notifiedAt && <span>· notified {timeAgo(c.notifiedAt)}</span>}
        {c.stripePaymentIntentId && (
          <a
            href={`https://dashboard.stripe.com/payments/${c.stripePaymentIntentId}`}
            target="_blank"
            rel="noreferrer"
            className="hover:text-primary-500 underline"
          >
            Stripe ↗
          </a>
        )}
      </div>

      {available.length > 0 && (
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          {available.map((action) => (
            <button
              key={action}
              className={`${ACTION_BTN} ${action === 'charge' ? 'text-red-600 dark:text-red-400' : ''}`}
              disabled={actions.isPending}
              onClick={() => (action === 'excuse' ? setExcusing((v) => !v) : actions.run(action, [c]))}
            >
              {action === 'charge' ? `Charge ${feeOf(c)} now` : ACTION_LABELS[action]}
            </button>
          ))}
        </div>
      )}

      {excusing && (
        <ExcuseForm
          pending={actions.isPending}
          onCancel={() => setExcusing(false)}
          onConfirm={async (reason) => {
            if (await actions.run('excuse', [c], reason)) setExcusing(false);
          }}
        />
      )}
    </div>
  );
}

export default function AdminOpsNoShows() {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [filters, setFilters] = useState<AdminAPI.GetNoShowsRequest>({});
  const setFilter = <K extends keyof AdminAPI.GetNoShowsRequest>(key: K, value: AdminAPI.GetNoShowsRequest[K]) =>
    setFilters((f) => ({ ...f, [key]: value || undefined }));
  const { data, isLoading, isFetching, refetch } = useNoShows(filters);
  const run = useRunNoShowReconciler();

  const config = data?.data?.config;
  const mode = config?.mode ?? 'off';
  const cases = data?.data?.cases ?? [];
  const counts = data?.data?.counts ?? {};
  const hasFilters = Object.values(filters).some(Boolean);
  const actions = useCaseActions();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkExcusing, setBulkExcusing] = useState(false);
  const selectable = cases.filter((c) => actionsFor(c).length > 0);
  const selected = cases.filter((c) => selectedIds.has(c.id));
  const allSelected = selectable.length > 0 && selectable.every((c) => selectedIds.has(c.id));
  const toggle = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // Bulk actions offered = the intersection of what every selected case allows.
  const bulkActions = (['warn', 'fee_notice', 'charge', 'excuse', 'reopen'] as const).filter((a) =>
    selected.length > 0 && selected.every((c) => actionsFor(c).includes(a))
  );
  const runBulk = async (action: AdminAPI.NoShowBatchAction, reason?: string) => {
    if (await actions.run(action, selected, reason)) {
      setSelectedIds(new Set());
      setBulkExcusing(false);
    }
  };

  const runNow = async (dryRun: boolean) => {
    if (!dryRun) {
      const ok = await confirm({
        title: 'Run the no-show pass for real?',
        message: 'This sends any due notices and takes any due charges right now, exactly as the hourly cron would.',
        confirmText: 'Run now',
        type: 'danger',
      });
      if (!ok) return;
    }
    run.mutate(dryRun, { onError: (err) => toast.error(errorMessage(err, 'Run failed')) });
  };
  const runResult = run.data?.data;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-4">
        <Link href="/ops" className="text-dark-muted hover:text-primary-500">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <UserX className="w-5 h-5 text-primary-500" />
        <h1 className="text-xl font-bold">No-show fees</h1>
        <RefreshButton onClick={() => refetch()} isFetching={isFetching} />
      </div>

      {config && (
        <div className="mb-6 rounded-xl border border-neutral-200 dark:border-neutral-700 p-3 text-sm">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-dark-muted">Mode</span>
            <span className="font-semibold capitalize">{mode}</span>
            <span className="text-dark-muted ml-3">Fee</span>
            <span className="font-semibold">
              {Object.entries(config.fees)
                .map(([currency, amount]) => formatPrice(amount, currency))
                .join(' · ')}
            </span>
            <span className="text-dark-muted ml-3">Grace period</span>
            <span className="font-semibold">{config.graceHours}h</span>
            <span className="text-dark-muted ml-3">Reports needed</span>
            <span className="font-semibold">{config.minReports}</span>
          </div>
          <p className="mt-2 text-xs text-dark-muted">{MODE_HELP[mode]}</p>
          <p className="mt-1 text-[11px] text-dark-muted">
            Set in server/src/services/noShow/config.ts. The mode can be overridden with NO_SHOW_FEE_MODE in the
            server environment.
          </p>
        </div>
      )}

      <div className="flex items-center gap-2 mb-2">
        <h2 className="text-sm font-semibold mr-auto">Cases</h2>
        <button className={BTN} onClick={() => runNow(true)} disabled={run.isPending}>
          <Play className="w-3.5 h-3.5" />
          Dry run
        </button>
        <button className={BTN} onClick={() => runNow(false)} disabled={run.isPending}>
          Run now
        </button>
      </div>

      {runResult && (
        <div className="mb-3 rounded-xl border border-neutral-200 dark:border-neutral-700 p-3 text-xs">
          <p className="font-medium">
            {runResult.dryRun ? 'Dry run' : 'Run'}: {runResult.detected} detected, {runResult.notified} notified,{' '}
            {runResult.charged} charged, {runResult.failed} failed
          </p>
          {runResult.details.map((d, i) => (
            <p key={i} className="text-dark-muted">
              {d}
            </p>
          ))}
        </div>
      )}

      <div className="flex gap-1.5 mb-2 flex-wrap">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.label}
            onClick={() => setFilter('status', f.key)}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${
              filters.status === f.key
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-600'
                : 'border-neutral-300 dark:border-neutral-600 text-dark-muted hover:border-primary-400'
            }`}
          >
            {f.label}
            {f.key && counts[f.key] ? ` ${counts[f.key]}` : ''}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-3 flex-wrap text-xs">
        <input
          className={INPUT}
          placeholder="Name or email"
          value={filters.q ?? ''}
          onChange={(e) => setFilter('q', e.target.value)}
        />
        <select
          className={INPUT}
          value={filters.disclaimer ?? ''}
          onChange={(e) => setFilter('disclaimer', (e.target.value || undefined) as AdminAPI.GetNoShowsRequest['disclaimer'])}
        >
          <option value="">Any disclaimer</option>
          <option value="with">With disclaimer</option>
          <option value="without">Without disclaimer</option>
        </select>
        <label className="flex items-center gap-1 text-dark-muted">
          Dinner from
          <input type="date" className={INPUT} value={filters.from ?? ''} onChange={(e) => setFilter('from', e.target.value)} />
        </label>
        <label className="flex items-center gap-1 text-dark-muted">
          to
          <input type="date" className={INPUT} value={filters.to ?? ''} onChange={(e) => setFilter('to', e.target.value)} />
        </label>
        {hasFilters && (
          <button className="text-primary-500 font-medium hover:underline" onClick={() => setFilters({})}>
            Clear
          </button>
        )}
      </div>

      {selectable.length > 0 && (
        <div className="mb-3 flex items-center gap-2 flex-wrap text-xs">
          <label className="inline-flex items-center gap-1.5 text-dark-muted">
            <input
              type="checkbox"
              className="checkbox checkbox-sm checkbox-primary"
              checked={allSelected}
              onChange={() => setSelectedIds(allSelected ? new Set() : new Set(selectable.map((c) => c.id)))}
            />
            {selected.length > 0 ? `${selected.length} selected` : 'Select all'}
          </label>
          {selected.length > 0 && (
            <>
              {bulkActions.map((action) => (
                <button
                  key={action}
                  className={`${ACTION_BTN} ${action === 'charge' ? 'text-red-600 dark:text-red-400' : ''}`}
                  disabled={actions.isPending}
                  onClick={() => (action === 'excuse' ? setBulkExcusing((v) => !v) : runBulk(action))}
                >
                  {action === 'charge' ? `Charge ${feeLabel(selected)} now` : ACTION_LABELS[action]}
                </button>
              ))}
              {bulkActions.length === 0 && <span className="text-dark-muted">No action applies to all selected cases.</span>}
              <button className="text-primary-500 font-medium hover:underline" onClick={() => setSelectedIds(new Set())}>
                Clear selection
              </button>
            </>
          )}
        </div>
      )}
      {bulkExcusing && selected.length > 0 && (
        <div className="mb-3">
          <ExcuseForm pending={actions.isPending} onCancel={() => setBulkExcusing(false)} onConfirm={(reason) => runBulk('excuse', reason)} />
        </div>
      )}

      {isLoading && <p className="text-sm text-dark-muted">Loading…</p>}
      {!isLoading && cases.length === 0 && (
        <p className="text-sm text-dark-muted">{hasFilters ? 'No cases match these filters.' : 'No cases.'}</p>
      )}
      <div className="space-y-2">
        {cases.map((c) => (
          <CaseRow key={c.id} c={c} mode={mode} selected={selectedIds.has(c.id)} onToggle={() => toggle(c.id)} actions={actions} />
        ))}
      </div>
    </div>
  );
}
